import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import * as _ from 'lodash';
import { ProjectModel } from '../../../../../core/services/project.model.service';
import { TrackerService } from '../../../services/tracker.service';
import { NotificationService } from '../../../../../core/services/core.notification.service';
import { StateService } from '@uirouter/angular';
import { TrackeritemService } from '../../../services/trackeritem.service';

@Component({
  selector: 'ti-edit-header',
  template: require('./trackeritem.edit-header.component.html')
})
export class TiEditHeaderComponent implements OnInit, OnChanges, OnDestroy {
  monitoring = false;
  @Input() project: any;
  @Input() projectMenu: any;
  @Input() trackeritem: any;
  @Input() acls: any;

  summaryEditMode: boolean;
  scmModule: boolean;
  scmPermission: number;
  duplicates: any[];
  duplicateOf: any[];
  isDetails: boolean;
  isCommits: boolean;
  isMerge: boolean;
  isRelated: boolean;
  isBreakdown: boolean;
  isNotebook: boolean;
  counts: { commits: number, related: number, breakdown: number, notebooks: number };

  constructor(
    private projectModel: ProjectModel,
    private stateService: StateService, 
    private trackerService: TrackerService,
    private trackeritemService: TrackeritemService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.summaryEditMode = true;
    this.setTab();
    this.duplicates = [];
    this.duplicateOf = [];
    this.counts = { commits: 0, related: 0, breakdown: 0, notebooks: 0 };

    // Initializes SCM module and permissions
    if (this.project) {
      this.scmModule = !_.isEmpty(this.projectModel.getEnabledScm(this.project));
      this.scmPermission = this.getScmPermission(this.project);
    }

    this.notificationService.subscribe('ti-edit', 'TRACKER.ITEM.EDIT.DUPLICATES.UPDATE', (duplicates) => {
      this.duplicates = duplicates;
      this.trackeritem.rel.duplicates.duplicatedBy = duplicates;
      this.updateCounts();
    });

    this.notificationService.subscribe('ti-edit', 'TRACKER_ITEM.EDIT.UPDATED', (ti) => {
      this.trackeritem = this.trackeritemService.updateTIValues(this.trackeritem, ti);
      if (_.has(ti, 'trackeritem.rel.attachments')) {
        this.trackeritem.rel.attachments = ti?.trackeritem?.rel?.attachments;
        this.updateCounts();
      }
      this.loadTimelineData();
    });

    this.loadTimelineData();
  }

  private loadTimelineData() {
    this.trackeritemService.fetchTimeline(this.trackeritem.id)
    .then(
      (data) =>  {
        this.trackeritem.rel = _.assignIn(this.trackeritem.rel, _.get(data, 'rel', {}));
        this.updateCounts();
      },
      (result) => {
        console.log(result);
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.trackeritem) {
      this.updateCounts();

      this.duplicates = _.get(this.trackeritem, 'rel.duplicates.duplicatedBy', []);
      this.duplicateOf = _.get(this.trackeritem, 'rel.duplicates.duplicateOf', []);  
    }

    if (changes.project) {
      this.scmModule = !_.isEmpty(this.projectModel.getEnabledScm(this.project));
      this.scmPermission = this.getScmPermission(this.project);
    }
  }

  ngOnDestroy(): void {
    this.notificationService.unsubscribe('ti-edit');
  }

  setTab() {
    this.isDetails = this.stateService.current.name == 'home.project.summary.tracker.item.edit';
    this.isCommits = this.stateService.current.name == 'home.project.summary.tracker.item.edit.commits';
    this.isMerge = this.stateService.current.name == 'home.project.summary.tracker.item.edit.merge';
    this.isRelated = this.stateService.current.name == 'home.project.summary.tracker.item.edit.related';
    this.isBreakdown = this.stateService.current.name == 'home.project.summary.tracker.item.edit.breakdown';
    this.isNotebook = this.stateService.current.name == 'home.project.summary.tracker.item.edit.notebook';
  }

  openTab(tabName: string) {
    let stateName: string = '';
    switch(tabName) {
      case 'details':
        stateName = 'home.project.summary.tracker.item.edit';
        break;

      case 'commits':
        stateName = 'home.project.summary.tracker.item.edit.commits';
        break;

      case 'merge':
        stateName = 'home.project.summary.tracker.item.edit.merge';
        break;

      case 'related':
        stateName = 'home.project.summary.tracker.item.edit.related';
        break;

      case 'breakdown':
        stateName = 'home.project.summary.tracker.item.edit.breakdown';
        break;

      case 'notebook':
        stateName = 'home.project.summary.tracker.item.edit.notebook';
        break;
    }

    if (stateName != '') {
      this.stateService.go(stateName)
      .then(() => {
        this.setTab();
      });
    }
  }

  priorityDesc(): string {
    return this.trackerService.fields.builtIn.priority.getDescription(this.trackeritem.priority);
  }

  priorityBadge(): string {
    return this.getCssClassForPriority(this.trackeritem.priority);
  }

  statusDescription(): string {
    return this.trackeritem?.extraFields?.status?.fieldData?.elementName ?? null;
  }

  private updateCounts() {
    this.counts = {
      commits: this.getCommitsCount(),
      related: this.getRelatedCount(),
      breakdown: this.getBreakdownCount(),
      notebooks: this.getNotebookCount()
    };
  }

  private getScmPermission(project: any): number {
    const perms = _.get(project, 'rel.permissions', {});
    return Object.keys(perms).reduce((carry, item) => {
        if (_.startsWith(item, 'scm')) {
            const val = perms[item];
            if (val > carry) return val;
        }
        return carry;
    }, -1);
  }

  private getCommitsCount = () => {
    return _.get(this.trackeritem, 'rel.commits.length', '');
  }

  private getNotebookCount = () => {
    return _.get(this.trackeritem, 'rel.notebooks.length', 0)
         + _.get(this.trackeritem, 'rel.attachments.length', 0);
  }

  // subitemsCount is a plain, DB-trigger-maintained top-level field on every TrackerItem --
  // unlike commits/notebooks, no extra rel fetch is needed to know this count.
  private getBreakdownCount = () => {
    return _.get(this.trackeritem, 'subitemsCount', 0);
  }

  private getRelatedCount = () => {
    return _.get(this.trackeritem, 'rel.dependencies.dependentOn.length', 0)
        + _.get(this.trackeritem, 'rel.dependencies.dependentUponBy.length', 0)
        + _.get(this.trackeritem, 'rel.associations.trackeritem.length', 0)
        + _.get(this.trackeritem, 'rel.associations.forummessage.length', 0)
        + _.get(this.trackeritem, 'rel.associations.docmanfolder.length', 0)
        + _.get(this.trackeritem, 'rel.associations.docmanfile.length', 0)
        + _.get(this.trackeritem, 'rel.associations.wikipage.length', 0)
        + _.get(this.trackeritem, 'rel.duplicates.duplicatedBy.length', 0);
    // TODO - associations are not displayed yet
    // TODO - duplicates are not available in API yet
    // + _.get(this.trackeritem, 'rel.dependencies.duplicateOf.length', 0)
    // + _.get(this.trackeritem, 'rel.dependencies.duplicateBy.length', 0)
  }

  public getCssClassForPriority(id) {
    // RMT: This used to be const, which doesn't do what you might think it does.
    // Specifically, the var cannot be re-assigned, but its value can be changed.
    // So instead of using it with (possibly) mistaken assumptions, we'll use var.
    // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/const
    var PRIORITY_CLASSES = {
        1: 'alert-danger',
        2: 'alert-danger',
        3: 'alert-warning',
        4: 'alert-info',
        5: 'alert-success'
    };
    
    return _.get(PRIORITY_CLASSES, id, 'alert-success');
  }
}
